/* ==========================================================================
   Protected Route
   Task F9/F19: Route wrapper with slug-based redirect and pharmacy validation
   ========================================================================== */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePortal } from '@/context/PortalContext'

interface ProtectedRouteProps {
  /** The protected content to render when authenticated */
  children: React.ReactNode
}

/**
 * Route wrapper that ensures the user is authenticated AND belongs to the current pharmacy.
 * Redirects to login page if not authenticated, preserving the intended destination.
 * Shows an error if the user belongs to a different pharmacy.
 *
 * @example
 * ```tsx
 * <Route
 *   path="/prescription"
 *   element={
 *     <ProtectedRoute>
 *       <PrescriptionForm />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading: authLoading, user } = useAuth()
  const {
    slug,
    config,
    loading: portalLoading,
    error: portalError,
  } = usePortal()
  const location = useLocation()

  // Show loading state while checking authentication or loading portal config
  if (authLoading || portalLoading) {
    return <AuthLoadingState />
  }

  // If portal config failed to load (invalid slug), show error
  if (portalError || !config) {
    return <PharmacyNotFoundState slug={slug} />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Preserve the intended destination for redirect after login
    const redirectTo = location.pathname + location.search
    return (
      <Navigate
        to={`/rx/${slug}/login?redirect=${encodeURIComponent(redirectTo)}`}
        replace
      />
    )
  }

  // Validate that user belongs to this pharmacy
  if (user.pharmacy_id !== config.id) {
    return (
      <WrongPharmacyState
        userPharmacyId={user.pharmacy_id}
        currentSlug={slug}
      />
    )
  }

  // Force a first-login password change when the pharmacy set the password
  // on the provider's behalf (PORTAL-75). Runs before RequireProfile so the
  // password change comes first. The change-password page itself is exempt to
  // avoid a redirect loop.
  if (user.must_change_password && !location.pathname.endsWith('/change-password')) {
    return <Navigate to={`/rx/${slug}/change-password`} replace />
  }

  // Conversely, a provider who is NOT flagged must never sit on the forced
  // change-password page — send them into the portal. This makes /change-password
  // reachable only while must_change_password is true (PORTAL-75). Without it the
  // page renders for any authenticated user who lands on that URL (e.g. a leftover
  // URL after switching accounts), even though /me reports false.
  if (!user.must_change_password && location.pathname.endsWith('/change-password')) {
    return <Navigate to={`/rx/${slug}/prescribe`} replace />
  }

  // User is authenticated and belongs to this pharmacy - render the protected content
  return <>{children}</>
}

/**
 * Loading state shown while checking authentication.
 */
function AuthLoadingState() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige">
      <div className="flex flex-col items-center gap-4">
        {/* Spinner */}
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
        <p className="text-sm text-charcoal/70">Loading...</p>
      </div>
    </div>
  )
}

/**
 * Error state shown when pharmacy slug is invalid.
 */
function PharmacyNotFoundState({ slug }: { slug: string | null }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="max-w-md text-center">
        <div className="mb-6 text-6xl">🏥</div>
        <h1 className="mb-2 font-serif text-2xl text-charcoal">
          Pharmacy Not Found
        </h1>
        <p className="mb-6 text-charcoal/70">
          {slug ? (
            <>
              The pharmacy portal{' '}
              <span className="font-mono font-medium">{slug}</span> does not
              exist or is no longer available.
            </>
          ) : (
            'No pharmacy portal specified.'
          )}
        </p>
        <a
          href="/"
          className="inline-block rounded-lg bg-deep-purple px-6 py-2 text-white hover:bg-deep-purple/90"
        >
          Go to Home
        </a>
      </div>
    </div>
  )
}

/**
 * Error state shown when user is logged in but to a different pharmacy.
 */
function WrongPharmacyState({
  userPharmacyId,
  currentSlug,
}: {
  userPharmacyId: string
  currentSlug: string | null
}) {
  // Log this for debugging (but don't expose pharmacy ID to user)
  console.warn(
    `User pharmacy mismatch: user belongs to ${userPharmacyId}, but accessing ${currentSlug}`
  )

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="max-w-md text-center">
        <div className="mb-6 text-6xl">🔒</div>
        <h1 className="mb-2 font-serif text-2xl text-charcoal">
          Access Denied
        </h1>
        <p className="mb-6 text-charcoal/70">
          Your account is not associated with this pharmacy portal. Please log
          in with an account registered to this pharmacy, or return to your own
          portal.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a
            href={`/rx/${currentSlug}/login`}
            className="inline-block rounded-lg bg-deep-purple px-6 py-2 text-white hover:bg-deep-purple/90"
          >
            Log In to This Pharmacy
          </a>
          <a
            href="/"
            className="inline-block rounded-lg border border-charcoal/20 px-6 py-2 text-charcoal hover:bg-charcoal/5"
          >
            Go to Home
          </a>
        </div>
      </div>
    </div>
  )
}

export default ProtectedRoute
